import { useCallback, useEffect, useMemo, useState } from "react";
import { api, fmtDate, parseApiError } from "@/lib/api";
import { PageHeader } from "./AdminLayout";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DataStateRow } from "@/components/ui/data-state";
import { toast } from "sonner";
import {
  UserPlus, CheckCircle2, Clock4, AlertTriangle, ChevronRight, Search, Plus, RefreshCw, X,
} from "lucide-react";

const CATEGORY_LABELS = {
  hr: "🧾 HR",
  it: "💻 IT",
  manager: "👔 Manager",
  finance: "💰 Kế toán",
  facilities: "🏢 Cơ sở vật chất",
  general: "📋 Khác",
};

const PROGRESS_TONE = (pct) => {
  if (pct >= 100) return "bg-emerald-600";
  if (pct >= 70) return "bg-sky-600";
  if (pct >= 30) return "bg-amber-500";
  return "bg-stone-400";
};

export default function Onboarding() {
  const [data, setData] = useState([]); // array of {employee, items, progress}
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [stats, setStats] = useState({ active: 0, completed: 0, overdue: 0 });

  const [initOpen, setInitOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [empQ, setEmpQ] = useState("");
  const [empId, setEmpId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get("/onboarding");
      setData(r.data.items || []);
      setStats(r.data.stats || { active: 0, completed: 0, overdue: 0 });
      if (!selectedId && r.data.items?.length) setSelectedId(r.data.items[0].employee.id);
    } catch (e) {
      setError(parseApiError(e, "Không tải được onboarding").message);
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!initOpen) return;
    api.get("/employees", { params: { limit: 500 } }).then((r) => setEmployees(r.data)).catch(() => {});
  }, [initOpen]);

  const selected = useMemo(() => data.find((d) => d.employee.id === selectedId) || data[0] || null, [data, selectedId]);

  const filteredEmps = useMemo(() => {
    const k = empQ.trim().toLowerCase();
    const onboardingIds = new Set(data.map((d) => d.employee.id));
    return employees
      .filter((e) => !onboardingIds.has(e.id))
      .filter((e) => !k || e.full_name?.toLowerCase().includes(k) || e.code?.toLowerCase().includes(k))
      .slice(0, 50);
  }, [employees, empQ, data]);

  const toggleItem = async (item) => {
    try {
      await api.patch(`/onboarding/items/${item.id}`, { completed: !item.completed });
      load();
    } catch (e) {
      toast.error(parseApiError(e, "Không cập nhật được").message);
    }
  };

  const initOnboarding = async () => {
    if (!empId) return;
    try {
      await api.post(`/employees/${empId}/onboarding/init`);
      toast.success("Đã khởi tạo onboarding");
      setInitOpen(false);
      setEmpId(null);
      setEmpQ("");
      load();
    } catch (e) {
      toast.error(parseApiError(e, "Không khởi tạo được").message);
    }
  };

  return (
    <div data-testid="page-onboarding">
      <PageHeader
        kicker="HR-Ops"
        title="Onboarding Tracker"
        breadcrumb={["Tổ chức", "Onboarding"]}
        actions={
          <>
            <Button variant="outline" onClick={load} className="rounded-sm">
              <RefreshCw className="w-4 h-4 mr-1.5" /> Tải lại
            </Button>
            <Button onClick={() => setInitOpen(true)} className="bg-emerald-800 hover:bg-emerald-900 rounded-sm" data-testid="btn-init-onboarding">
              <UserPlus className="w-4 h-4 mr-1.5" /> Khởi tạo cho NV
            </Button>
          </>
        }
      />

      <div className="p-6 lg:p-8 space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <StatCard label="Đang onboarding" value={stats.active} sub="Chưa hoàn thành" tone="sky" icon={<Clock4 className="w-4 h-4" />} />
          <StatCard label="Đã hoàn thành" value={stats.completed} sub="Hoàn thành 100%" tone="emerald" icon={<CheckCircle2 className="w-4 h-4" />} />
          <StatCard label="Quá hạn" value={stats.overdue} sub="Có task vượt due date" tone="amber" icon={<AlertTriangle className="w-4 h-4" />} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left: employee list */}
          <div className="lg:col-span-1 bg-white border border-stone-200 rounded-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-stone-200 flex items-center gap-2">
              <div className="text-xs font-bold uppercase tracking-wider text-stone-600">NV đang onboarding</div>
              <div className="ml-auto text-xs text-stone-500 font-mono">{data.length}</div>
            </div>
            <div className="divide-y divide-stone-100 max-h-[600px] overflow-y-auto">
              {loading && <div className="p-6 text-center text-stone-500 text-sm">Đang tải...</div>}
              {!loading && error && (
                <div className="p-6 text-center text-red-700 text-sm">
                  {error}
                  <div className="mt-2">
                    <Button size="sm" variant="outline" onClick={load} className="rounded-sm">Thử lại</Button>
                  </div>
                </div>
              )}
              {!loading && !error && data.length === 0 && (
                <div className="p-6 text-center text-stone-500 text-sm">
                  <UserPlus className="w-8 h-8 mx-auto text-stone-300 mb-2" />
                  Chưa có ai đang onboarding
                </div>
              )}
              {data.map((d) => {
                const active = selected?.employee.id === d.employee.id;
                return (
                  <button
                    key={d.employee.id}
                    onClick={() => setSelectedId(d.employee.id)}
                    className={`w-full px-4 py-3 text-left flex items-center gap-3 hover:bg-stone-50 ${
                      active ? "bg-emerald-50" : ""
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-stone-900 truncate">{d.employee.full_name}</div>
                      <div className="text-[11px] text-stone-500 font-mono">{d.employee.code}</div>
                      <div className="flex items-center gap-2 mt-1.5">
                        <div className="flex-1 bg-stone-200 rounded-full h-1.5 overflow-hidden">
                          <div
                            className={`h-full ${PROGRESS_TONE(d.progress)}`}
                            style={{ width: `${d.progress}%` }}
                          />
                        </div>
                        <span className="text-[11px] font-mono text-stone-600">{d.progress}%</span>
                      </div>
                    </div>
                    <ChevronRight className={`w-4 h-4 ${active ? "text-emerald-700" : "text-stone-300"}`} />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right: detail */}
          <div className="lg:col-span-2 bg-white border border-stone-200 rounded-sm overflow-hidden">
            {selected ? (
              <OnboardingDetail data={selected} onToggle={toggleItem} />
            ) : (
              <div className="p-8 text-center text-stone-500">
                Chọn một nhân viên ở bên trái để xem checklist
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={initOpen} onOpenChange={setInitOpen}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Khởi tạo onboarding</DialogTitle>
          </DialogHeader>
          <div>
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input
                autoFocus
                placeholder="Tìm nhân viên..."
                value={empQ}
                onChange={(e) => setEmpQ(e.target.value)}
                className="pl-8 h-9 rounded-sm"
              />
            </div>
            <div className="max-h-[300px] overflow-y-auto border border-stone-200 rounded-sm divide-y divide-stone-100 mt-3">
              {filteredEmps.length === 0 && (
                <div className="text-center text-stone-500 text-sm py-6">
                  Không có nhân viên phù hợp (hoặc đã có onboarding rồi)
                </div>
              )}
              {filteredEmps.map((e) => (
                <button
                  key={e.id}
                  onClick={() => setEmpId(e.id)}
                  className={`w-full px-3 py-2 text-left flex items-center justify-between hover:bg-stone-50 ${
                    empId === e.id ? "bg-emerald-50" : ""
                  }`}
                >
                  <div>
                    <div className="font-semibold text-sm text-stone-900">{e.full_name}</div>
                    <div className="text-[11px] text-stone-500 font-mono">{e.code}</div>
                  </div>
                  {empId === e.id && <CheckCircle2 className="w-4 h-4 text-emerald-700" />}
                </button>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInitOpen(false)} className="rounded-sm">
              Hủy
            </Button>
            <Button
              onClick={initOnboarding}
              disabled={!empId}
              className="bg-emerald-800 hover:bg-emerald-900 rounded-sm"
            >
              Khởi tạo checklist
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OnboardingDetail({ data, onToggle }) {
  const { employee, items, progress } = data;
  const grouped = useMemo(() => {
    const map = {};
    items.forEach((it) => {
      const k = it.category || "general";
      if (!map[k]) map[k] = [];
      map[k].push(it);
    });
    return Object.entries(map);
  }, [items]);

  return (
    <div className="flex flex-col h-full max-h-[600px]">
      <div className="px-5 py-4 border-b border-stone-200 flex-shrink-0">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div>
            <div className="font-serif text-xl font-bold text-stone-900">{employee.full_name}</div>
            <div className="text-xs text-stone-500 font-mono">
              {employee.code} · {employee.position || "—"}
            </div>
            <div className="text-xs text-stone-600 mt-1">
              Ngày vào: {employee.hire_date ? fmtDate(employee.hire_date) : "—"}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500 font-bold">Tiến độ</div>
            <div className="font-mono text-2xl font-bold text-emerald-800">{progress}%</div>
          </div>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        {grouped.length === 0 && (
          <div className="text-center text-stone-500 text-sm py-8">Chưa có task nào</div>
        )}
        {grouped.map(([category, list]) => {
          const done = list.filter((i) => i.completed).length;
          return (
            <div key={category}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-xs font-bold text-stone-700">{CATEGORY_LABELS[category] || category}</div>
                <div className="text-[11px] text-stone-500 font-mono">
                  {done}/{list.length}
                </div>
              </div>
              <div className="border border-stone-200 rounded-sm divide-y divide-stone-100">
                {list.map((item) => (
                  <div key={item.id} className={`px-3 py-2 flex items-center gap-3 ${item.completed ? "bg-stone-50/50" : ""}`}>
                    <Checkbox
                      checked={item.completed}
                      onCheckedChange={() => onToggle(item)}
                      data-testid={`onb-item-${item.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm ${item.completed ? "text-stone-400 line-through" : "text-stone-900"}`}>
                        {item.title}
                      </div>
                      {item.owner_role && (
                        <div className="text-[11px] text-stone-500 mt-0.5">
                          Phụ trách: <span className="font-semibold">{item.owner_role}</span>
                          {item.due_offset_days != null && ` · Trong ${item.due_offset_days} ngày`}
                        </div>
                      )}
                    </div>
                    {item.completed_at && (
                      <span className="text-[11px] text-stone-500 font-mono">{fmtDate(item.completed_at)}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, sub, tone }) {
  const toneClass =
    tone === "emerald"
      ? "border-l-emerald-600"
      : tone === "sky"
      ? "border-l-sky-600"
      : tone === "amber"
      ? "border-l-amber-500"
      : "border-l-stone-400";
  const iconBg =
    tone === "emerald"
      ? "bg-emerald-50 text-emerald-700"
      : tone === "sky"
      ? "bg-sky-50 text-sky-700"
      : tone === "amber"
      ? "bg-amber-50 text-amber-700"
      : "bg-stone-100 text-stone-700";
  return (
    <div className={`bg-white border border-stone-200 border-l-4 ${toneClass} rounded-sm px-4 py-3`}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500 font-bold">{label}</div>
        <div className={`w-7 h-7 rounded-sm flex items-center justify-center ${iconBg}`}>{icon}</div>
      </div>
      <div className="font-serif text-3xl font-bold text-stone-900 mt-1 tracking-tight">{value}</div>
      <div className="text-[11px] text-stone-500 mt-0.5">{sub}</div>
    </div>
  );
}
